import React from 'react';
import { PiUserCircleLight } from 'react-icons/pi';
import apis from '../apis';
import type { CollaboratorAdapter } from './types';

interface ClientLawyer {
  uuid: string;
  user_id?: number | string;
  first_name?: string;
  last_name?: string;
  name?: string;
  email?: string;
  profile_picture?: string | null;
  initials?: string | null;
  is_lead?: boolean;
  role?: string;
  access_level?: string;
}

interface BuildClientAdapterArgs {
  clientId: string;
  lawyers: ClientLawyer[];
  currentUserUuid?: string;
  canAdmin?: boolean;
  onUpdate?: () => void;
  subMessage?: string;
}

const fullName = (l: ClientLawyer): string =>
  l.name || `${l.first_name || ''} ${l.last_name || ''}`.trim() || l.email || '';

const initialsOf = (l: ClientLawyer): string => {
  if (l.initials) return l.initials;
  const f = (l.first_name || '').charAt(0).toUpperCase();
  const last = (l.last_name || '').charAt(0).toUpperCase();
  return (f + last).slice(0, 2) || (l.email?.charAt(0).toUpperCase() || '??');
};

// Normalize legacy long-form access values written by older backend rows.
const backendToUi = (raw?: string): 'view' | 'edit' | 'admin' => {
  if (raw === 'edit' || raw === 'edit_access') return 'edit';
  if (raw === 'admin' || raw === 'full_access') return 'admin';
  return 'view';
};

export const buildClientCollaboratorAdapter = ({
  clientId,
  lawyers,
  currentUserUuid,
  canAdmin,
  onUpdate,
  subMessage,
}: BuildClientAdapterArgs): CollaboratorAdapter => {
  const collaborators = lawyers.map((l) => ({
    user_id: l.user_id ?? l.uuid,
    collaborator_uuid: l.uuid,
    name: fullName(l),
    email: l.email || '',
    initials: initialsOf(l),
    profile_picture: l.profile_picture || null,
    access: backendToUi(l.access_level || l.role),
    source: 'direct',
    is_lead: !!l.is_lead,
  }));

  return {
    resourceUuid: clientId,
    resourceType: 'client',
    collaborators,
    currentUserUuid,
    canAdmin,
    onUpdate,
    addCollaborator: (uuid, userIdOrUuid, access) =>
      apis.post(`/clients/${uuid}/lawyers`, {
        lawyer_id: String(userIdOrUuid),
        lawyer_role: access || 'view',
      }),
    removeCollaborator: (uuid, collaboratorUuid) =>
      apis.delete(`/clients/${uuid}/lawyers/${collaboratorUuid}`),
    updateAccess: (uuid, collaboratorUuid, access) =>
      apis.put(`/clients/${uuid}/lawyers/${collaboratorUuid}`, {
        access_level: access || 'view',
      }),
    setLead: (uuid, collaboratorUuid) =>
      apis.put(`/clients/${uuid}/lawyers/${collaboratorUuid}`, { is_lead: true }),
    supportsLead: true,
    isLead: (c: any) => !!c?.is_lead,
    ownerLabel: 'Lead',
    icon: React.createElement(PiUserCircleLight, { className: 'w-5 h-5' }),
    subMessage,
  };
};
